import { useState, useEffect } from 'react';
import { Save, Upload, X, Building2, MapPin, Wrench, Activity, Image as ImageIcon, StickyNote, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';
import apiClient from '../../core/apiClient';

interface CompanyUser { id: string; firstName: string; lastName: string; }
interface CatalogItem { id: number; code: string; name: string; isActive: number; }

interface EquipmentForm {
  descripcio: string;
  descripcion: string;
  grup: string;
  subgrup: string;
  ambit: string;
  marca: string;
  model: string;
  numeroSerie: string;
  marcatCe: number;
  potencia: string;
  voltatge: string;
  pes: number | '';
  importCompra: number | '';
  valorResidual: number | '';
  ubicacio: string;
  dataFab: string;
  dataCompra: string;
  dataBaixa: string;
  idProveidor: number | '';
  assignatUserId: string;
  necessitaManteniment: number;
  plaMant: string;
  mesosManteniments: number | '';
  titolPlaManteniment: string;
  tipusPlaManteniment: string;
  vencimentPlaManteniment: string;
  proveidorMantenimentUserId: string;
  responsableMantenimentUserId: string;
  necessitaConsum: number;
  tipusUs: string;
  foto1: string;
  foto2: string;
  foto3: string;
  fotoMarcatgeCe: string;
  utProvi: string;
  observacions: string;
  isActive: number;
}

const EMPTY_FORM = (): EquipmentForm => ({
  descripcio: '', descripcion: '', grup: '', subgrup: '', ambit: '',
  marca: '', model: '', numeroSerie: '', marcatCe: 0, potencia: '', voltatge: '',
  pes: '', importCompra: '', valorResidual: '', ubicacio: '',
  dataFab: '', dataCompra: '', dataBaixa: '', idProveidor: '',
  assignatUserId: '', necessitaManteniment: 0, plaMant: '', mesosManteniments: '',
  titolPlaManteniment: '', tipusPlaManteniment: '', vencimentPlaManteniment: '',
  proveidorMantenimentUserId: '', responsableMantenimentUserId: '',
  necessitaConsum: 0, tipusUs: '', foto1: '', foto2: '', foto3: '', fotoMarcatgeCe: '',
  utProvi: '', observacions: '', isActive: 1,
});

const inputCls = 'w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';

function PhotoField({ label, url, onUpload, onClear, onZoom }: { label: string; url: string; onUpload: (f: File) => void; onClear: () => void; onZoom: (url: string) => void }) {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onUpload(f);
  };
  const handleDragOver = (e: React.DragEvent<HTMLElement>) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  return (
    <div>
      <label className={labelCls}>{label}</label>
      {url ? (
        <div
          className="relative w-full h-32 border border-gray-200 rounded overflow-hidden bg-gray-50"
          onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
        >
          <img src={url} alt={label} className="w-full h-full object-contain cursor-zoom-in" onClick={() => onZoom(url)} />
          {dragOver && <div className="absolute inset-0 bg-blue-500/10 border-2 border-blue-400 border-dashed pointer-events-none" />}
          <button type="button" onClick={onClear} className="absolute top-1 right-1 bg-white/90 rounded p-1 hover:bg-red-50 text-gray-700 hover:text-red-700">
            <X size={14} />
          </button>
        </div>
      ) : (
        <label
          className={`flex flex-col items-center justify-center w-full h-32 border border-dashed rounded cursor-pointer hover:bg-gray-50 text-muted-foreground transition-colors ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`}
          onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
        >
          <Upload size={18} />
          <span className="text-xs mt-1 text-center">Pujar o arrossegar foto</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }} />
        </label>
      )}
    </div>
  );
}

export default function InfraestructuraForm({ equipmentId, onBack }: { equipmentId: string; onBack: () => void }) {
  const isNew = equipmentId === 'new';
  const [form, setForm] = useState<EquipmentForm>(EMPTY_FORM());
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [grups, setGrups] = useState<CatalogItem[]>([]);
  const [subgrups, setSubgrups] = useState<CatalogItem[]>([]);
  const [ambits, setAmbits] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<'foto1' | 'foto2' | 'foto3' | 'fotoMarcatgeCe' | null>(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [ubicacioOpen, setUbicacioOpen] = useState(false);
  const [mantenimentOpen, setMantenimentOpen] = useState(false);
  const [usOpen, setUsOpen] = useState(false);
  const [fotosOpen, setFotosOpen] = useState(true);
  const [observacionsOpen, setObservacionsOpen] = useState(false);

  useEffect(() => {
    api.get<{ success: boolean; data: CompanyUser[] }>('/auth/users').then((r) => setUsers(r.data ?? [])).catch(() => setUsers([]));
    api.get<{ success: boolean; data: CatalogItem[] }>('/catalog/grups-equipament').then((r) => setGrups((r.data ?? []).filter((g) => g.isActive))).catch(() => {});
    api.get<{ success: boolean; data: CatalogItem[] }>('/catalog/subgrups-equipament').then((r) => setSubgrups((r.data ?? []).filter((g) => g.isActive))).catch(() => {});
    api.get<{ success: boolean; data: CatalogItem[] }>('/catalog/ambits-equipament').then((r) => setAmbits((r.data ?? []).filter((g) => g.isActive))).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    api.get<{ success: boolean; data: any }>(`/taller/infraestructura/${equipmentId}`)
      .then((r) => {
        const e = r.data;
        setForm({
          descripcio: e.descripcio ?? '', descripcion: e.descripcion ?? '',
          grup: e.grup ?? '', subgrup: e.subgrup ?? '', ambit: e.ambit ?? '',
          marca: e.marca ?? '', model: e.model ?? '', numeroSerie: e.numeroSerie ?? '',
          marcatCe: Number(e.marcatCe ?? 0), potencia: e.potencia ?? '', voltatge: e.voltatge ?? '',
          pes: e.pes ?? '', importCompra: e.importCompra ?? '', valorResidual: e.valorResidual ?? '',
          ubicacio: e.ubicacio ?? '',
          dataFab: e.dataFab ? String(e.dataFab).split('T')[0] : '',
          dataCompra: e.dataCompra ? String(e.dataCompra).split('T')[0] : '',
          dataBaixa: e.dataBaixa ? String(e.dataBaixa).split('T')[0] : '',
          idProveidor: e.idProveidor ?? '',
          assignatUserId: e.assignatUserId ?? '',
          necessitaManteniment: Number(e.necessitaManteniment ?? 0),
          plaMant: e.plaMant ?? '', mesosManteniments: e.mesosManteniments ?? '',
          titolPlaManteniment: e.titolPlaManteniment ?? '', tipusPlaManteniment: e.tipusPlaManteniment ?? '',
          vencimentPlaManteniment: e.vencimentPlaManteniment ? String(e.vencimentPlaManteniment).split('T')[0] : '',
          proveidorMantenimentUserId: e.proveidorMantenimentUserId ?? '',
          responsableMantenimentUserId: e.responsableMantenimentUserId ?? '',
          necessitaConsum: Number(e.necessitaConsum ?? 0), tipusUs: e.tipusUs ?? '',
          foto1: e.foto1 ?? '', foto2: e.foto2 ?? '', foto3: e.foto3 ?? '', fotoMarcatgeCe: e.fotoMarcatgeCe ?? '',
          utProvi: e.utProvi ?? '', observacions: e.observacions ?? '',
          isActive: Number(e.isActive ?? 1),
        });
      })
      .catch(() => alert('Error carregant l\'equip'))
      .finally(() => setLoading(false));
  }, [equipmentId, isNew]);

  const handleUpload = async (field: 'foto1' | 'foto2' | 'foto3' | 'fotoMarcatgeCe', file: File) => {
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post('/taller/infraestructura/upload-image', fd, { headers: { 'Content-Type': undefined } });
      setForm((f) => ({ ...f, [field]: res.data.data.imageUrl }));
    } catch {
      alert('Error pujant la foto');
    } finally {
      setUploading(null);
    }
  };

  const handleSave = async () => {
    if (!form.descripcio.trim()) { alert('Cal indicar la descripció'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        pes: form.pes === '' ? undefined : Number(form.pes),
        importCompra: form.importCompra === '' ? undefined : Number(form.importCompra),
        valorResidual: form.valorResidual === '' ? undefined : Number(form.valorResidual),
        idProveidor: form.idProveidor === '' ? undefined : Number(form.idProveidor),
        mesosManteniments: form.mesosManteniments === '' ? undefined : Number(form.mesosManteniments),
        dataFab: form.dataFab || null,
        dataCompra: form.dataCompra || null,
        dataBaixa: form.dataBaixa || null,
        vencimentPlaManteniment: form.vencimentPlaManteniment || null,
        assignatUserId: form.assignatUserId || null,
        proveidorMantenimentUserId: form.proveidorMantenimentUserId || null,
        responsableMantenimentUserId: form.responsableMantenimentUserId || null,
        foto1: form.foto1 || null,
        foto2: form.foto2 || null,
        foto3: form.foto3 || null,
        fotoMarcatgeCe: form.fotoMarcatgeCe || null,
      };
      if (isNew) {
        const res = await api.post<{ success: boolean; data: any }>('/taller/infraestructura', payload);
        if (res.success) onBack();
      } else {
        await api.put(`/taller/infraestructura/${equipmentId}`, payload);
        onBack();
      }
    } catch (err: any) {
      alert('Error desant: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setSaving(false);
    }
  };

  const userOptions = (value: string, onChange: (v: string) => void) => (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— Selecciona —</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
    </select>
  );

  if (loading) return <div className="p-8 text-center text-muted-foreground text-sm">Carregant...</div>;

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">{isNew ? 'Nou Equip' : form.descripcio}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">
            Cancel·lar
          </button>
          <Button onClick={handleSave} disabled={saving}>
            <Save size={14} className="mr-1.5" /> {saving ? 'Desant...' : 'Desar'}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
      <div className="grid grid-cols-2 gap-6">

      <div className="space-y-6">
        {/* Dades generals */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Building2 size={16} className="text-blue-600" /> Dades generals
            </h2>
          </div>
          <div className="p-5 grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className={labelCls}>Descripció *</label>
              <Input className={inputCls} value={form.descripcio} onChange={(e) => setForm((f) => ({ ...f, descripcio: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Descripció (castellà)</label>
              <Input className={inputCls} value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Marca</label>
              <Input className={inputCls} value={form.marca} onChange={(e) => setForm((f) => ({ ...f, marca: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Model</label>
              <Input className={inputCls} value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Número de sèrie</label>
              <Input className={inputCls} value={form.numeroSerie} onChange={(e) => setForm((f) => ({ ...f, numeroSerie: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Potència</label>
              <Input className={inputCls} value={form.potencia} onChange={(e) => setForm((f) => ({ ...f, potencia: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Voltatge</label>
              <Input className={inputCls} value={form.voltatge} onChange={(e) => setForm((f) => ({ ...f, voltatge: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Pes (kg)</label>
              <Input type="number" step="0.001" className={inputCls} value={form.pes} onChange={(e) => setForm((f) => ({ ...f, pes: e.target.value === '' ? '' : Number(e.target.value) }))} />
            </div>
            <div>
              <label className={labelCls}>Grup</label>
              <select className={inputCls} value={form.grup} onChange={(e) => setForm((f) => ({ ...f, grup: e.target.value }))}>
                <option value="">— Selecciona —</option>
                {grups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Subgrup</label>
              <select className={inputCls} value={form.subgrup} onChange={(e) => setForm((f) => ({ ...f, subgrup: e.target.value }))}>
                <option value="">— Selecciona —</option>
                {subgrups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Àmbit</label>
              <select className={inputCls} value={form.ambit} onChange={(e) => setForm((f) => ({ ...f, ambit: e.target.value }))}>
                <option value="">— Selecciona —</option>
                {ambits.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
              </select>
            </div>
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.marcatCe} onChange={(e) => setForm((f) => ({ ...f, marcatCe: e.target.checked ? 1 : 0 }))} />
                Marcat CE
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Fotos */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <button type="button" onClick={() => setFotosOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
              {fotosOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <ImageIcon size={16} className="text-purple-600" /> Fotos
            </button>
          </div>
          {fotosOpen && (
          <div className="p-5">
            <div className="grid grid-cols-4 gap-3">
              <PhotoField label="Foto 1" url={form.foto1} onUpload={(f) => handleUpload('foto1', f)} onClear={() => setForm((f) => ({ ...f, foto1: '' }))} onZoom={setZoomUrl} />
              <PhotoField label="Foto 2" url={form.foto2} onUpload={(f) => handleUpload('foto2', f)} onClear={() => setForm((f) => ({ ...f, foto2: '' }))} onZoom={setZoomUrl} />
              <PhotoField label="Foto 3" url={form.foto3} onUpload={(f) => handleUpload('foto3', f)} onClear={() => setForm((f) => ({ ...f, foto3: '' }))} onZoom={setZoomUrl} />
              <PhotoField label="Marcatge CE" url={form.fotoMarcatgeCe} onUpload={(f) => handleUpload('fotoMarcatgeCe', f)} onClear={() => setForm((f) => ({ ...f, fotoMarcatgeCe: '' }))} onZoom={setZoomUrl} />
            </div>
            {uploading && <p className="text-xs text-muted-foreground mt-2">Pujant foto...</p>}
          </div>
          )}
        </div>

        {/* Ubicació, valoració i assignació */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <button type="button" onClick={() => setUbicacioOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
              {ubicacioOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <MapPin size={16} className="text-orange-600" /> Ubicació, valoració i assignació
            </button>
          </div>
          {ubicacioOpen && (
          <div className="p-5 grid grid-cols-3 gap-2">
            <div>
              <label className={labelCls}>Ubicació</label>
              <Input className={inputCls} value={form.ubicacio} onChange={(e) => setForm((f) => ({ ...f, ubicacio: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Assignat a</label>
              {userOptions(form.assignatUserId, (v) => setForm((f) => ({ ...f, assignatUserId: v })))}
            </div>
            <div>
              <label className={labelCls}>Id proveïdor (referència antiga)</label>
              <Input type="number" className={inputCls} value={form.idProveidor} onChange={(e) => setForm((f) => ({ ...f, idProveidor: e.target.value === '' ? '' : Number(e.target.value) }))} />
            </div>
            <div>
              <label className={labelCls}>Data fabricació</label>
              <Input type="date" className={inputCls} value={form.dataFab} onChange={(e) => setForm((f) => ({ ...f, dataFab: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Data compra</label>
              <Input type="date" className={inputCls} value={form.dataCompra} onChange={(e) => setForm((f) => ({ ...f, dataCompra: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Data baixa</label>
              <Input type="date" className={inputCls} value={form.dataBaixa} onChange={(e) => setForm((f) => ({ ...f, dataBaixa: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Import compra (€)</label>
              <Input type="number" step="0.01" className={inputCls} value={form.importCompra} onChange={(e) => setForm((f) => ({ ...f, importCompra: e.target.value === '' ? '' : Number(e.target.value) }))} />
            </div>
            <div>
              <label className={labelCls}>Valor residual (€)</label>
              <Input type="number" step="0.01" className={inputCls} value={form.valorResidual} onChange={(e) => setForm((f) => ({ ...f, valorResidual: e.target.value === '' ? '' : Number(e.target.value) }))} />
            </div>
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked ? 1 : 0 }))} />
                Actiu
              </label>
            </div>
          </div>
          )}
        </div>

        {/* Manteniment */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <button type="button" onClick={() => setMantenimentOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
              {mantenimentOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Wrench size={16} className="text-green-600" /> Manteniment i seguiment
            </button>
          </div>
          {mantenimentOpen && (
          <div className="p-5 grid grid-cols-3 gap-2">
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.necessitaManteniment} onChange={(e) => setForm((f) => ({ ...f, necessitaManteniment: e.target.checked ? 1 : 0 }))} />
                Necessita manteniment
              </label>
            </div>
            <div>
              <label className={labelCls}>Títol pla de manteniment</label>
              <Input className={inputCls} value={form.titolPlaManteniment} onChange={(e) => setForm((f) => ({ ...f, titolPlaManteniment: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Tipus de pla</label>
              <Input className={inputCls} value={form.tipusPlaManteniment} onChange={(e) => setForm((f) => ({ ...f, tipusPlaManteniment: e.target.value }))} placeholder="Preventiu, Correctiu..." />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Pla de manteniment</label>
              <Input className={inputCls} value={form.plaMant} onChange={(e) => setForm((f) => ({ ...f, plaMant: e.target.value }))} placeholder="Ex: Canvi d'oli cada 6 mesos" />
            </div>
            <div>
              <label className={labelCls}>Periodicitat (mesos)</label>
              <Input type="number" className={inputCls} value={form.mesosManteniments} onChange={(e) => setForm((f) => ({ ...f, mesosManteniments: e.target.value === '' ? '' : Number(e.target.value) }))} />
            </div>
            <div>
              <label className={labelCls}>Proper venciment</label>
              <Input type="date" className={inputCls} value={form.vencimentPlaManteniment} onChange={(e) => setForm((f) => ({ ...f, vencimentPlaManteniment: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Proveïdor manteniment</label>
              {userOptions(form.proveidorMantenimentUserId, (v) => setForm((f) => ({ ...f, proveidorMantenimentUserId: v })))}
            </div>
            <div>
              <label className={labelCls}>Responsable manteniment</label>
              {userOptions(form.responsableMantenimentUserId, (v) => setForm((f) => ({ ...f, responsableMantenimentUserId: v })))}
            </div>
          </div>
          )}
        </div>

        {/* Ús i consum */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <button type="button" onClick={() => setUsOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
              {usOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Activity size={16} className="text-blue-600" /> Ús i consum
            </button>
          </div>
          {usOpen && (
          <div className="p-5 grid grid-cols-3 gap-2">
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.necessitaConsum} onChange={(e) => setForm((f) => ({ ...f, necessitaConsum: e.target.checked ? 1 : 0 }))} />
                Necessita consum
              </label>
            </div>
            <div>
              <label className={labelCls}>Tipus d'ús</label>
              <Input className={inputCls} value={form.tipusUs} onChange={(e) => setForm((f) => ({ ...f, tipusUs: e.target.value }))} placeholder="Taller, Muntatge, Oficina..." />
            </div>
            <div>
              <label className={labelCls}>Unitat provisió</label>
              <Input className={inputCls} value={form.utProvi} onChange={(e) => setForm((f) => ({ ...f, utProvi: e.target.value }))} />
            </div>
          </div>
          )}
        </div>

        {/* Observacions */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <button type="button" onClick={() => setObservacionsOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
              {observacionsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <StickyNote size={16} className="text-gray-500" /> Observacions
            </button>
          </div>
          {observacionsOpen && (
          <div className="p-5">
            <textarea rows={3} className={inputCls} value={form.observacions} onChange={(e) => setForm((f) => ({ ...f, observacions: e.target.value }))} />
          </div>
          )}
        </div>
      </div>

      </div>
      </div>

      {zoomUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8"
          onClick={() => setZoomUrl(null)}
        >
          <img src={zoomUrl} alt="Vista ampliada" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            onClick={() => setZoomUrl(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <X size={28} />
          </button>
        </div>
      )}
    </div>
  );
}
